import { useQuery } from '@tanstack/react-query';

import { ApiError } from '@/lib/errors';
import { fetchWithDefaults } from '@/lib/http/client';

export interface OutlookStatusAccount {
  email: string | null;
  // null for orphaned/disconnected accounts (SocialAccount removed, CRM entity remains)
  social_account_id: number | null;
  outlook_mail_entity_id: string;
  outlook_calendar_entity_id: string;
  outlook_mail_connection_status: string | null;
  outlook_calendar_connection_status: string | null;
  outlook_mail_default_data_visibility: string | null;
  outlook_calendar_default_data_visibility: string | null;
  /** Primary send mailbox for this provider (carried on the outlook_mail integration). */
  is_primary: boolean;
  needs_reconnect: boolean;
}

export interface OutlookStatus {
  connected: boolean;
  needs_reconnect: boolean;
  accounts: OutlookStatusAccount[];
}

const STALE_TIME = 5 * 60_000;
const GC_TIME = 10 * 60_000;

async function fetchOutlookStatus(orgId: string): Promise<OutlookStatus> {
  const response = await fetchWithDefaults(
    `/users/api/outlook/status/?org_id=${orgId}`
  );
  if (!response.ok) {
    throw new ApiError(
      `Outlook status failed: ${response.status}`,
      response.status
    );
  }
  return response.json();
}

interface UseOutlookStatusOptions {
  enabled?: boolean;
}

export function useOutlookStatus(
  orgId: string | undefined,
  options?: UseOutlookStatusOptions
) {
  // Gate the auto fetch via `enabled` while still keeping the real orgId baked
  // into the query — so a manual `refetch()` works even when autoLoad is off
  // (mirrors useGmailStatus's (orgId, { enabled }) wiring).
  const enabled = (options?.enabled ?? true) && !!orgId;
  return useQuery({
    queryKey: ['microsoft', 'outlook', 'status', orgId],
    queryFn: () => {
      // `enabled` above gates the auto fetch, but defend against the cast so a
      // future regression can never put `?org_id=undefined` on the wire.
      if (!orgId) {
        throw new Error('useOutlookStatus.queryFn invoked without orgId');
      }
      return fetchOutlookStatus(orgId);
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled,
  });
}
